// Logo — компонент-обёртка для отображения эмблемы сервиса «Упряжка».
// Использует Jivo-логотип (public/jivo-logo.svg, 69x50 viewBox) с CSS-фильтром,
// переводящим чёрно-зелёный оригинал в корпоративный зелёный Jivo (#2BAC06).
// SVG импортируется как URL через Vite (?url) — современный способ получить статический asset.

import type { CSSProperties } from 'react';
import jivoLogoUrl from '/jivo-logo.svg?url';
import { useThemeMode } from '@/theme';

/**
 * CSS-фильтр, переводящий чёрный fill SVG-эмблемы в фирменный зелёный Jivo `#2BAC06`.
 * Цепочка: brightness(0) — обнуляет цвет до чёрного;
 *          saturate(100%) — выключает остаточную сатурацию;
 *          invert(36%) sepia(74%) saturate(2476%) hue-rotate(83deg) — даёт зелёный;
 *          brightness(96%) contrast(105%) — финальная подстройка.
 * Источник: https://codepen.io/sosuke/pen/Pjoqqp (color filter generator).
 */
const JIVO_GREEN_FILTER =
  'brightness(0) saturate(100%) invert(36%) sepia(74%) saturate(2476%) hue-rotate(83deg) brightness(96%) contrast(105%)';

interface LogoProps {
  /** Размер в px (по умолчанию 40). Ширина подбирается пропорционально 69:50. */
  size?: number;
  /** Дополнительные стили (опционально). */
  style?: CSSProperties;
  /** Alt-текст для accessibility. */
  alt?: string;
}

/**
 * Компонент эмблемы «Упряжка». Зелёный filter применяется всегда (в обеих темах),
 * чтобы логотип оставался узнаваемым брендовым элементом вне зависимости от темы.
 */
export function Logo({ size = 40, style, alt = 'Упряжка logo' }: LogoProps): JSX.Element {
  // useThemeMode используется только для accessibility-объявления alt (theme-aware не нужен,
  // но hook сохранён для будущего переключения интенсивности зелёного по теме).
  useThemeMode();

  const aspectRatio = 69 / 50;
  const width = Math.round(size * aspectRatio);
  const height = size;

  const mergedStyle: CSSProperties = {
    height,
    width,
    filter: JIVO_GREEN_FILTER,
    display: 'inline-block',
    verticalAlign: 'middle',
    ...style,
  };

  return <img src={jivoLogoUrl} alt={alt} style={mergedStyle} data-testid="sled-logo" />;
}
